/**
 * IndexedDB helper for offline data storage.
 * Stores emails, contacts, events, drafts, and outbox queue.
 */

const DB_NAME = 'promail-offline';
const DB_VERSION = 1;

const STORES = {
  emails: 'emails',
  contacts: 'contacts',
  events: 'events',
  drafts: 'drafts',
  outbox: 'outbox',
};

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORES.emails)) {
        const emailStore = db.createObjectStore(STORES.emails, { keyPath: 'id' });
        emailStore.createIndex('folder', 'folder', { unique: false });
        emailStore.createIndex('date', 'date', { unique: false });
        emailStore.createIndex('user_id', 'user_id', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.contacts)) {
        db.createObjectStore(STORES.contacts, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.events)) {
        db.createObjectStore(STORES.events, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.drafts)) {
        db.createObjectStore(STORES.drafts, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORES.outbox)) {
        db.createObjectStore(STORES.outbox, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// Generic CRUD operations

async function putAll(storeName, items) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  for (const item of items) {
    store.put(item);
  }
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function getAll(storeName) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);
  const request = store.getAll();
  return new Promise((resolve, reject) => {
    request.onsuccess = () => { db.close(); resolve(request.result); };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}

async function getByKey(storeName, key) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readonly');
  const store = tx.objectStore(storeName);
  const request = store.get(key);
  return new Promise((resolve, reject) => {
    request.onsuccess = () => { db.close(); resolve(request.result); };
    request.onerror = () => { db.close(); reject(request.error); };
  });
}

async function putItem(storeName, item) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  store.put(item);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function deleteItem(storeName, key) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  store.delete(key);
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function clearStore(storeName) {
  const db = await openDB();
  const tx = db.transaction(storeName, 'readwrite');
  const store = tx.objectStore(storeName);
  store.clear();
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

// ============= PUBLIC API =============

export const offlineDb = {
  // Emails
  cacheEmails: (emails) => putAll(STORES.emails, emails),
  getCachedEmails: () => getAll(STORES.emails),
  getCachedEmailsByFolder: async (folder) => {
    const all = await getAll(STORES.emails);
    const folderMap = {
      'INBOX': ['INBOX', 'Boîte de réception'],
      'Sent': ['Sent', 'Envoyés'],
      'Drafts': ['Drafts', 'Brouillons'],
      'Trash': ['Trash', 'Corbeille'],
      'Spam': ['Spam', 'Junk'],
    };
    const variants = folderMap[folder] || [folder];
    return all
      .filter((e) => variants.includes(e.folder))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  },
  getCachedEmail: (id) => getByKey(STORES.emails, id),

  // Contacts
  cacheContacts: (contacts) => putAll(STORES.contacts, contacts),
  getCachedContacts: () => getAll(STORES.contacts),

  // Events
  cacheEvents: (events) => putAll(STORES.events, events),
  getCachedEvents: () => getAll(STORES.events),

  // Drafts (saved offline)
  saveDraft: (draft) => putItem(STORES.drafts, { ...draft, id: draft.id || `draft_${Date.now()}` }),
  getDrafts: () => getAll(STORES.drafts),
  deleteDraft: (id) => deleteItem(STORES.drafts, id),

  // Outbox (emails to send when back online)
  addToOutbox: (email) => putItem(STORES.outbox, { ...email, id: `outbox_${Date.now()}`, queued_at: new Date().toISOString() }),
  getOutbox: () => getAll(STORES.outbox),
  removeFromOutbox: (id) => deleteItem(STORES.outbox, id),
  clearOutbox: () => clearStore(STORES.outbox),

  // Clear all cached data
  clearAll: async () => {
    await clearStore(STORES.emails);
    await clearStore(STORES.contacts);
    await clearStore(STORES.events);
    await clearStore(STORES.drafts);
    await clearStore(STORES.outbox);
  },
};
